import { useRef, useState } from "react";
import { LocateIcon, Upload } from "lucide-react";
import CustomInput from "../../shared/CustomInput";
import LoanApplicationModal from "../../shared/LoanApplicationModal";
import { uploadImage } from "../../sdk/upload/upload";

const BusinessLocation = ({
  closeBusinessLocation,
  activeIndex,
  handleNextStep,
  borrowerBusinessInfo,
  setBorrowerBusinessInfo,
  loading,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const [showCoordinates, setShowCoordinates] = useState(false);
  const [fetchingLocation, setFetchingLocation] = useState(false);

  const handleChooseFileClick = () => fileInputRef.current.click();

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const response = await uploadImage(file);
      setBorrowerBusinessInfo({
        ...borrowerBusinessInfo,
        businessImageUrl: response.url,
      });
      setSelectedFile(file);
    }
  };

  const getLocation = () => {
    setFetchingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setBorrowerBusinessInfo({
          ...borrowerBusinessInfo,
          businessLatitude: position.coords.latitude,
          businessLongitude: position.coords.longitude,
        });
        setShowCoordinates(true);
        setFetchingLocation(false);
      },
      (error) => {
        console.error("Error getting location", error);
        setFetchingLocation(false);
      },
    );
  };

  return (
    <LoanApplicationModal
      closeBioInformation={closeBusinessLocation}
      activeIndex={activeIndex}
      handleNextStep={handleNextStep}
      loading={loading}
    >
      <p className="text-[#000000] font-normal text-[14px]">
        Create a New Loan Application
      </p>
      <p className="text-[#000000] font-semibold text-[20px]">
        Borrower’s Business Location
      </p>
      <p className="text-[#5C5C5C] text-[14px] font-normal">
        Enter borrower’s business location details
      </p>

      {/* Button to get current location */}
      {!showCoordinates && (
        <div className="my-4">
          <button
            onClick={getLocation}
            className="text-[#fff] bg-[#000000] text-[14px] h-[35px] flex items-center justify-center gap-[10px] rounded-[30px] px-[20px] font-medium"
            disabled={fetchingLocation}
          >
            <LocateIcon size={16} color="#fff" />
            {fetchingLocation
              ? "Fetching location..."
              : "Use my current location"}
          </button>
        </div>
      )}

      {/* Show inputs after location is accepted */}
      {showCoordinates && (
        <div className="flex items-center gap-[20px] my-[10px]">
          <div className="flex w-[45%] flex-col">
            <label className="text-[#222222] font-medium text-[13px]">
              Business Latitude
            </label>
            <CustomInput
              placeholder="Latitude"
              type="number"
              value={borrowerBusinessInfo?.businessLatitude || ""}
              onChange={(e) =>
                setBorrowerBusinessInfo({
                  ...borrowerBusinessInfo,
                  businessLatitude: e.target.value,
                })
              }
            />
          </div>
          <div className="flex w-[45%] flex-col">
            <label className="text-[#222222] font-medium text-[13px]">
              Business Longitude
            </label>
            <CustomInput
              placeholder="Longitude"
              type="number"
              value={borrowerBusinessInfo?.businessLongitude || ""}
              onChange={(e) =>
                setBorrowerBusinessInfo({
                  ...borrowerBusinessInfo,
                  businessLongitude: e.target.value,
                })
              }
            />
          </div>
        </div>
      )}

      {/* location details */}
      <div className="flex items-center gap-[20px] my-[10px]">
        <div className="flex w-[45%] flex-col">
          <label className="text-[#222222] font-medium text-[13px]">
            Location Link
          </label>
          <CustomInput
            placeholder="Enter location link"
            type="text"
            value={borrowerBusinessInfo?.businessImageUrl}
            onChange={(e) =>
              setBorrowerBusinessInfo({
                ...borrowerBusinessInfo,
                businessImageUrl: e.target.value,
              })
            }
          />
        </div>
        <div className="flex w-[45%] flex-col">
          <label className="text-[#222222] font-medium text-[13px]">
            Physical Address
          </label>
          <CustomInput
            placeholder="Enter physical address"
            type="text"
            value={borrowerBusinessInfo?.businessPhysicalAddress}
            onChange={(e) =>
              setBorrowerBusinessInfo({
                ...borrowerBusinessInfo,
                businessPhysicalAddress: e.target.value,
              })
            }
          />
        </div>
      </div>

      {/* location description */}
      <div className="flex items-center gap-[20px] my-[10px]">
        <div className="flex w-[45%] flex-col">
          <label className="text-[#222222] font-medium text-[13px]">
            Location Description
          </label>
          <CustomInput
            placeholder="Enter location description"
            type="text"
            value={borrowerBusinessInfo?.businessLocationDescription}
            onChange={(e) =>
              setBorrowerBusinessInfo({
                ...borrowerBusinessInfo,
                businessLocationDescription: e.target.value,
              })
            }
          />
        </div>
      </div>

      {/* photo upload */}
      <div className="flex w-[45%] my-[20px] flex-col">
        <p className="text-[#222222] font-medium text-[13px]">
          Business Location Photo{" "}
          <span className="font-normal text-[#5C5C5C]">
            .jpeg or .png file only
          </span>
        </p>
        <div
          className="h-[90px] flex flex-col justify-center items-center gap-[10px] rounded-[8px] border-[0.7px] border-[#A8A8A8] bg-white cursor-pointer"
          onClick={handleChooseFileClick}
        >
          {selectedFile ? (
            <div className="text-center text-[12px] text-black">
              <p className="font-semibold">{selectedFile.name}</p>
              <p className="text-gray-500">
                {(selectedFile.size / 1024).toFixed(2)} KB
              </p>
            </div>
          ) : (
            <>
              <Upload />
              <p className="text-[#000000] text-[12px] font-normal">
                Drag and Drop file here or{" "}
                <span className="text-[#000000] underline">Choose file</span>{" "}
                here
              </p>
            </>
          )}
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      </div>
    </LoanApplicationModal>
  );
};

export default BusinessLocation;
